<template>
  <div
    v-if="type === 'number' && controls"
    class="app-input-wrapper app-input-number controls"
    :class="{ small, fullWidth }"
  >
    <app-button
      @click.stop="stepDown"
      icon
      :disabled="stepDownDisabled"
      noShadow
      >-</app-button
    >

    <input
      v-model="inputValue"
      :type="inputType"
      :inputmode="inputMode"
      ref="input"
      v-on="omit($listeners, ['input'])"
      v-bind="$attrs"
      class="app-input"
      :min="min"
      :max="maxReachable"
      :style="{
        width: `${length}ch`,
      }"
      :maxlength="maxLength"
      :required="required"
      :readonly="canEdit === false"
      :autocomplete="autocomplete"
    />

    <app-button @click.stop="stepUp" icon :disabled="stepUpDisabled" noShadow
      >+</app-button
    >
  </div>

  <textarea
    v-else-if="type === 'textarea'"
    v-model="inputValue"
    ref="input"
    v-on="omit($listeners, ['input'])"
    v-bind="$attrs"
    class="app-input textarea"
    :class="{
      'full-width': fullWidth,
    }"
    :required="required"
    :maxlength="maxLength"
    :autocomplete="autocomplete"
  />

  <!-- <div
    v-else-if="clearButton"
    class="app-input-wrapper"
    :class="{ 'show-clear-button': showClearButton }"
  >
    <app-input-group>
      <input
        v-model="inputValue"
        :type="inputType"
        :inputMode="inputMode"
        ref="input"
        v-on="omit($listeners, ['input'])"
        v-bind="$attrs"
        class="app-input"
        :required="required"
        :min="min"
        :max="max"
        :maxlength="maxLength"
      />

      <app-button @click="clearInput" icon tabindex="-1">
        <app-icon>clear</app-icon>
      </app-button>
    </app-input-group>
  </div> -->

  <input
    v-else
    ref="input"
    :type="inputType"
    :inputmode="inputMode"
    v-model="inputValue"
    @focus="onFocus"
    @blur="onBlur"
    v-on="omit($listeners, ['input'])"
    v-bind="$attrs"
    class="app-input"
    :required="required"
    :min="min"
    :max="max"
    :maxlength="maxLength"
    :autocomplete="autocomplete"
    autocorrect="off"
    autocapitalize="off"
    spellcheck="false"
  />
</template>

<script>
import { sanitizePhoneNumber, getFormattedPhoneNumber, omit } from '@/helpers'
import Inputmask from 'inputmask'

const inputModes = {
  price: 'decimal',
  money: 'decimal',
  currency: 'decimal',

  number: 'numeric',
  numeric: 'numeric',
  cardNumber: 'numeric',

  phone: 'tel',
  telephone: 'tel',
  tel: 'tel',
}

export default {
  inheritAttrs: false,
  name: 'AppInput',

  props: {
    type: {
      type: String,
      default: 'text',
    },

    controls: {
      type: Boolean,
      default: false,
    },

    value: {
      type: String | Number,
    },

    clearButton: {
      type: Boolean,
      default: true,
    },

    mask: {
      type: Array | String,
      required: false,
    },

    small: {
      type: Boolean,
      default: false,
    },

    canEdit: {
      type: Boolean,
      default: false,
    },
  },

  watch: {
    maskOptions: 'maskInput',
  },

  computed: {
    autocomplete() {
      return this.$attrs.autocomplete ?? 'off'
    },

    isNumberInput() {
      return ['number', 'numeric'].includes(this.type)
    },

    min() {
      const min = this.$attrs.min

      if (min !== undefined) return parseFloat(min)
    },

    max() {
      let max = this.$attrs.max

      if (max === undefined && this.isNumberInput) {
        if (this.maxLength) max = ''.padStart(this.maxLength, '9')
      }

      if (max !== undefined) return parseFloat(max)
    },

    maxReachable() {
      return Math.floor(this.max / this.step) * this.step
    },

    maxLength() {
      const maxLength = parseInt(this.$attrs.maxlength ?? this.$attrs.maxLength)

      if (isNaN(maxLength) === false) return maxLength
    },

    outputToNumber() {
      return ['money', 'currency', 'number'].includes(this.type)
    },

    inputValue: {
      get() {
        const postValueFunction = this.$attrs.postValue

        let value = this.value

        if (typeof postValueFunction === 'function')
          value = postValueFunction(value)
        else {
          switch (this.inputType) {
            case 'tel':
              value = getFormattedPhoneNumber(value)
              break
          }
        }

        return value ?? ''
      },

      async set(newValue) {
        const oldValue = this.inputValue
        const beforeInput = this.$listeners.beforeInput

        if (typeof beforeInput === 'function') {
          const listenerResult = await beforeInput(newValue, oldValue)

          if (listenerResult === false) return
        }

        const isInputNumberControls = this.type === 'number' && this.controls

        if (isInputNumberControls) {
          if (newValue > this.maxReachable) newValue = this.maxReachable
          else if (newValue < this.min) newValue = this.min
        }

        if (this.isNumberInput && this.maxLength >= 0)
          newValue = newValue.toString().slice(0, this.maxLength)

        if (this.outputToNumber === true) {
          newValue = this.getNumber(newValue)

          if (isNaN(newValue)) newValue = undefined
        }

        switch (this.inputType) {
          case 'tel':
            newValue = sanitizePhoneNumber(newValue)
            break
        }

        const beforeSet = this.$listeners.beforeSet

        if (typeof beforeSet === 'function')
          newValue = await beforeSet(newValue, oldValue)

        this.$emit('input', newValue, oldValue)

        if (!this.maskOptions && this.inputType !== 'tel') {
          const { input: $input } = this.$refs

          if ($input) {
            const currentInputValue = $input.value
            const newInputValue = (newValue ?? '').toString()

            if (currentInputValue !== newInputValue)
              $input.value = newInputValue
          }
        }

        if (isInputNumberControls) {
          if (newValue > oldValue) this.$emit('stepUp', newValue)
          else if (newValue < oldValue) this.$emit('stepDown', newValue)
        }
      },
    },

    showClearButton() {
      return this.clearButton === true && this.length > 0
    },

    length() {
      return this.inputValue?.toString?.()?.length ?? 0
    },

    inputContainer() {
      let parent = this.$parent

      while (parent && parent.$options.name !== 'AppInputContainer')
        parent = parent.$parent

      return parent
    },

    inputType() {
      switch (this.type) {
        // case 'number':
        // case 'numeric':
        // case 'cardNumber':
        //   return this.$attrs.postValue ? 'text' : 'number'
        //   break

        // case 'monthYear':
        case 'phone':
        case 'telephone':
        case 'tel':
          return 'tel'
          break

        case 'email':
        case 'password':
        case 'url':
        case 'search':
          return this.type
          break

        default:
          return 'text'
          break
      }
    },

    inputMode() {
      let inputMode =
        this.$attrs.inputmode ??
        this.$attrs.inputMode ??
        this.$attrs['input-mode']

      if (!inputMode) {
        const { type } = this

        inputMode = inputModes[type]
      }

      return inputMode
    },

    maskOptions() {
      let mask = this.mask || {}

      switch (this.type) {
        case 'phone':
        case 'telephone':
        case 'tel':
          mask = {
            mask: ['(99) 9999-9999', '(99) 9 9999-9999'],
            onBeforeMask: value => value.replace(/[^0-9]/g, ''),
            ...mask,
          }
          break

        case 'currency':
        case 'money':
        case 'price':
          mask = {
            alias: 'currency',
            allowPlus: false,
            allowMinus: true,
            rightAlign: false,
            groupSeparator: '.',
            numericInput: true,
            radixPoint: ',',
            digits: 2,
            prefix: 'R$ ',
            unmaskAsNumber: true,
            onBeforeMask: (value, opts) => {
              value = this.getNumber(value) || 0

              return value.toFixed(2).toString()
            },
            ...mask,
          }
          break

        // case 'cardNumber':
        //   mask = {
        //     mask: [
        //       '9999 9999 9999 9999 999', // 4-4-4-4-3
        //       '9999 9999 9999 9999', // 4-4-4-4
        //       '9999 9999 99999', // 4-4-5
        //       '9999 999999 9999', // 4-6-4
        //       '999 999999 99999', // 3-6-5
        //       '9999 999999 99999', // 4-6-5
        //     ],
        //     placeholder: '',
        //     ...mask,
        //   }
        //   break

        case 'monthYear':
          mask = {
            mask: '99/9999',
            placeholder: 'MM/AAAA',
            ...mask,
          }
          break
      }

      if (Object.keys(mask).length === 0) mask = undefined

      return mask
    },

    isInputContainerFocused() {
      return this.inputContainer?.focused
    },

    required() {
      return this.inputContainer?.required || this.$attrs.required || false
    },

    step() {
      return parseFloat(this.$attrs.step || 1)
    },

    stepDownDisabled() {
      if (this.$attrs.stepDownDisabled) return true

      const min = this.min
      const newValue = this.getNumber(this.inputValue) - this.step

      return newValue < min
    },

    stepUpDisabled() {
      if (this.$attrs.stepUpDisabled) return true

      const max = this.max
      const newValue = this.getNumber(this.inputValue) + this.step

      return newValue > max
    },

    fullWidth() {
      return [true, ''].includes(
        this.$attrs.fullWidth ??
          this.$attrs['full-width'] ??
          this.$attrs.fluid ??
          false,
      )
    },
  },

  created() {
    const inputContainer = this.inputContainer

    if (inputContainer) {
      this.$on('focus', () => (inputContainer.focused = true))
      this.$on('blur', () => (inputContainer.focused = false))
    }
  },

  mounted() {
    this.maskInput()
  },

  methods: {
    omit,

    async onFocus(event) {
      this.$parent.$el.classList.add('app-input-focused')
      this.$emit('focus', event)
    },

    async onBlur(event) {
      this.$parent.$el.classList.remove('app-input-focused')
      this.$emit('blur', event)
    },

    getNumber(value) {
      if ([null, undefined].includes(value)) return
      if (typeof value !== 'string') value = value.toString()

      let regex = /[^0-9,]/g

      if (value.includes(',') === false) regex = /[^0-9.]/g

      return parseFloat(value.replace(regex, '').replace(',', '.'))
    },

    stepDown() {
      const newValue = this.getNumber(this.inputValue) - this.step

      this.inputValue = newValue
    },

    stepUp() {
      const newValue = this.getNumber(this.inputValue) + this.step

      this.inputValue = newValue
    },

    async maskInput(maskOptions = this.maskOptions) {
      if (!maskOptions) return

      const input = this.$refs.input

      if (!input) return

      return Inputmask(maskOptions).mask(input)
    },

    isCompleteMask() {
      const inputMask = this.$refs.input?.inputmask

      if (inputMask) return inputMask.isComplete()
    },

    blur() {
      return this.$refs.input.blur()
    },

    scrollTo() {
      const { input } = this.$refs

      return (
        input?.scrollIntoViewIfNeeded?.() ??
        input?.scrollIntoView?.({
          block: 'center',
          inline: 'center',
        })
      )
    },

    focus() {
      const { input } = this.$refs

      this.scrollTo()

      return input.focus()
    },

    select() {
      const { input } = this.$refs

      this.scrollTo()

      return input.select()
    },

    clearInput() {
      this.inputValue = ''
      this.focus()
    },
  },
}
</script>

<style lang="scss" scoped>
@use 'sass:math';

@import '@/styles/functions.scss';
@import '@/styles/settings.scss';

$borderRadius: math.div($defaultBorderRadius, 2);
$backgroundColor: rgba(0, 0, 0, 0.075);

.app-input {
  // $defaultColor: #f2f2f2;
  appearance: none;
  border-radius: $borderRadius;
  border: 0;
  margin: 0;
  padding: math.div($defaultPadding, 1.5);
  caret-color: $primaryColor;
  background-color: $backgroundColor;
  color: rgba(0, 0, 0, 0.55);
  outline: none;
  font-family: inherit;
  font-size: 0.85rem;
  display: block;
  width: 100%;
  box-sizing: border-box;
  transition: color $defaultTransitionDuration,
    background-color $defaultTransitionDuration;

  &:focus {
    color: $primaryColor;
    background-color: rgba($primaryColor, 0.1);
  }

  &::placeholder {
    color: rgba(0, 0, 0, 0.35);
  }

  &::selection {
    background-color: $primaryColor;
    color: get-text-color($primaryColor);
  }

  &::-webkit-search-cancel-button {
    appearance: none;
  }

  &[size] {
    width: auto;
  }

  &.full-width {
    width: 100%;
  }
}

.app-input-wrapper {
  position: relative;

  &.show-clear-button {
    > .app-input {
      padding-right: 50px;
    }
  }

  &.app-input-number {
    &.controls {
      $padding: math.div($defaultPadding, 1.5);

      display: flex;
      flex-direction: row;
      align-items: stretch;

      > * {
        border-radius: 0;

        &:first-child {
          border-top-left-radius: $borderRadius;
          border-bottom-left-radius: $borderRadius;
        }

        &:last-child {
          border-top-right-radius: $borderRadius;
          border-bottom-right-radius: $borderRadius;
        }
      }

      > input {
        min-width: 2ch;
        line-height: 100%;
        padding: $padding 0;
        margin-left: -1px;
        margin-right: -1px;
        text-align: center;
        font-weight: bold;
        color: #000;
        background-color: #ececec;
      }

      > .app-button {
        padding: $padding;
        // background-color: $backgroundColor;
        background-color: #ececec;
        font-weight: normal;
        color: $secondaryColor;
      }

      [disabled],
      .disabled {
        color: $mutedColor;
      }

      &.small {
        $padding: math.div($defaultPadding, 2);

        > .app-button {
          padding: $padding;
        }

        > input {
          padding: $padding 0;
        }
      }
    }
  }

  &.fullWidth {
    .app-input {
      flex: 1;
    }
  }

  .app-input-clear-button {
    border-top-right-radius: $borderRadius;
    border-bottom-right-radius: $borderRadius;
    position: absolute;
    padding: #{math.div($defaultPadding, 2)};
    top: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;

    > .app-button {
      margin: 0;
      padding: 5px;
      border-radius: 50%;
      font-size: 12px;
      transition: background-color $defaultTransitionDuration;

      > .app-icon {
        font-size: inherit;
      }
    }
  }
}
</style>
